import { memo, useMemo } from 'react';
import {
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  EdgeLabelRenderer,
  useViewport,
  useNodes,
  BaseEdge,
} from '@xyflow/react';

/**
 * Configuration for label positioning
 */
interface LabelConfig {
  /** Minimum zoom level to show labels */
  minZoomToShow: number;
  /** Zoom level below which to truncate labels */
  truncateZoom: number;
  /** Maximum characters when truncated */
  truncateLength: number;
  /** Padding around label background */
  bgPadding: { x: number; y: number };
  /** Border radius for label background */
  bgBorderRadius: number;
}

const DEFAULT_CONFIG: LabelConfig = {
  minZoomToShow: 0.3,
  truncateZoom: 0.6,
  truncateLength: 15,
  bgPadding: { x: 8, y: 4 },
  bgBorderRadius: 4,
};

/**
 * Calculate offset to avoid overlapping with nodes
 */
function calculateLabelOffset(
  labelX: number,
  labelY: number,
  labelWidth: number,
  labelHeight: number,
  nodes: Array<{ position: { x: number; y: number }; measured?: { width?: number; height?: number } }>,
  edgeDirection: 'horizontal' | 'vertical'
): { offsetX: number; offsetY: number } {
  const PADDING = 10;
  const labelBounds = {
    left: labelX - labelWidth / 2 - PADDING,
    right: labelX + labelWidth / 2 + PADDING,
    top: labelY - labelHeight / 2 - PADDING,
    bottom: labelY + labelHeight / 2 + PADDING,
  };

  for (const node of nodes) {
    const nodeWidth = node.measured?.width ?? 180;
    const nodeHeight = node.measured?.height ?? 60;
    const nodeBounds = {
      left: node.position.x,
      right: node.position.x + nodeWidth,
      top: node.position.y,
      bottom: node.position.y + nodeHeight,
    };

    // Check for overlap
    const overlapsX = labelBounds.right > nodeBounds.left && labelBounds.left < nodeBounds.right;
    const overlapsY = labelBounds.bottom > nodeBounds.top && labelBounds.top < nodeBounds.bottom;

    if (overlapsX && overlapsY) {
      // Calculate offset to move label outside node bounds
      if (edgeDirection === 'horizontal') {
        // Move label up or down
        const moveUp = Math.abs(labelBounds.bottom - nodeBounds.top);
        const moveDown = Math.abs(nodeBounds.bottom - labelBounds.top);
        return { offsetX: 0, offsetY: moveUp < moveDown ? -moveUp - 15 : moveDown + 15 };
      } else {
        // Move label left or right
        const moveLeft = Math.abs(labelBounds.right - nodeBounds.left);
        const moveRight = Math.abs(nodeBounds.right - labelBounds.left);
        return { offsetX: moveLeft < moveRight ? -moveLeft - 15 : moveRight + 15, offsetY: 0 };
      }
    }
  }

  return { offsetX: 0, offsetY: 0 };
}

// Removed unused calculateOptimalLabelPosition - label position is now handled by React Flow paths

/**
 * Smart Label Edge - Bezier variant
 * Uses bezier curves with intelligent label positioning
 */
export const SmartBezierEdge = memo(function SmartBezierEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  labelStyle,
  labelBgStyle,
  style,
  markerEnd,
  markerStart,
  data,
}: EdgeProps) {
  const { zoom } = useViewport();
  const nodes = useNodes();

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Determine edge direction for offset calculation
  const edgeDirection = Math.abs(targetX - sourceX) > Math.abs(targetY - sourceY)
    ? 'horizontal' as const
    : 'vertical' as const;

  // Calculate label dimensions (estimate based on text length)
  const labelText = String(label || '');
  const estimatedWidth = labelText.length * 7 + DEFAULT_CONFIG.bgPadding.x * 2;
  const estimatedHeight = 16 + DEFAULT_CONFIG.bgPadding.y * 2;

  // Calculate offset to avoid nodes
  const { offsetX, offsetY } = useMemo(() => {
    if (!label) return { offsetX: 0, offsetY: 0 };
    return calculateLabelOffset(
      labelX,
      labelY,
      estimatedWidth,
      estimatedHeight,
      nodes,
      edgeDirection
    );
  }, [label, labelX, labelY, estimatedWidth, estimatedHeight, nodes, edgeDirection]);

  // Add small perpendicular offset for better readability
  const perpendicularOffset = edgeDirection === 'horizontal' ? { x: 0, y: -12 } : { x: 12, y: 0 };

  // Calculate final label position
  const finalLabelX = labelX + offsetX + perpendicularOffset.x;
  const finalLabelY = labelY + offsetY + perpendicularOffset.y;

  // Zoom-responsive label visibility
  const showLabel = label && zoom >= DEFAULT_CONFIG.minZoomToShow;
  const displayLabel = useMemo(() => {
    if (!label) return '';
    const text = String(label);
    if (zoom < DEFAULT_CONFIG.truncateZoom && text.length > DEFAULT_CONFIG.truncateLength) {
      return text.substring(0, DEFAULT_CONFIG.truncateLength) + '...';
    }
    return text;
  }, [label, zoom]);

  // Get custom label styles from data
  const customLabelStyle = data?.labelStyle as React.CSSProperties | undefined;
  const customLabelBgStyle = data?.labelBgStyle as React.CSSProperties | undefined;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={style}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-auto"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${finalLabelX}px, ${finalLabelY}px)`,
              fontSize: 11,
              fontWeight: 500,
              background: 'white',
              padding: `${DEFAULT_CONFIG.bgPadding.y}px ${DEFAULT_CONFIG.bgPadding.x}px`,
              borderRadius: DEFAULT_CONFIG.bgBorderRadius,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              whiteSpace: 'nowrap',
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              zIndex: 1000,
              transition: 'opacity 0.2s ease',
              opacity: zoom < DEFAULT_CONFIG.minZoomToShow + 0.1 ? 0.7 : 1,
              ...labelBgStyle,
              ...customLabelBgStyle,
            }}
          >
            <span style={{ ...labelStyle, ...customLabelStyle }}>{displayLabel}</span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

/**
 * Smart Label Edge - SmoothStep variant
 * Uses orthogonal paths with rounded corners for cleaner diagrams
 */
export const SmartSmoothStepEdge = memo(function SmartSmoothStepEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  labelStyle,
  labelBgStyle,
  style,
  markerEnd,
  markerStart,
  data,
}: EdgeProps) {
  const { zoom } = useViewport();
  const nodes = useNodes();

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  // Determine edge direction for offset calculation
  const edgeDirection = Math.abs(targetX - sourceX) > Math.abs(targetY - sourceY)
    ? 'horizontal' as const
    : 'vertical' as const;

  // Calculate label dimensions
  const labelText = String(label || '');
  const estimatedWidth = labelText.length * 7 + DEFAULT_CONFIG.bgPadding.x * 2;
  const estimatedHeight = 16 + DEFAULT_CONFIG.bgPadding.y * 2;

  // Calculate offset to avoid nodes
  const { offsetX, offsetY } = useMemo(() => {
    if (!label) return { offsetX: 0, offsetY: 0 };
    return calculateLabelOffset(
      labelX,
      labelY,
      estimatedWidth,
      estimatedHeight,
      nodes,
      edgeDirection
    );
  }, [label, labelX, labelY, estimatedWidth, estimatedHeight, nodes, edgeDirection]);

  // Add perpendicular offset for better readability
  const perpendicularOffset = edgeDirection === 'horizontal' ? { x: 0, y: -12 } : { x: 12, y: 0 };

  // Calculate final label position
  const finalLabelX = labelX + offsetX + perpendicularOffset.x;
  const finalLabelY = labelY + offsetY + perpendicularOffset.y;

  // Zoom-responsive label visibility
  const showLabel = label && zoom >= DEFAULT_CONFIG.minZoomToShow;
  const displayLabel = useMemo(() => {
    if (!label) return '';
    const text = String(label);
    if (zoom < DEFAULT_CONFIG.truncateZoom && text.length > DEFAULT_CONFIG.truncateLength) {
      return text.substring(0, DEFAULT_CONFIG.truncateLength) + '...';
    }
    return text;
  }, [label, zoom]);

  // Get custom label styles from data
  const customLabelStyle = data?.labelStyle as React.CSSProperties | undefined;
  const customLabelBgStyle = data?.labelBgStyle as React.CSSProperties | undefined;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={style}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-auto"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${finalLabelX}px, ${finalLabelY}px)`,
              fontSize: 11,
              fontWeight: 500,
              background: 'white',
              padding: `${DEFAULT_CONFIG.bgPadding.y}px ${DEFAULT_CONFIG.bgPadding.x}px`,
              borderRadius: DEFAULT_CONFIG.bgBorderRadius,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              whiteSpace: 'nowrap',
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              zIndex: 1000,
              transition: 'opacity 0.2s ease',
              opacity: zoom < DEFAULT_CONFIG.minZoomToShow + 0.1 ? 0.7 : 1,
              ...labelBgStyle,
              ...customLabelBgStyle,
            }}
          >
            <span style={{ ...labelStyle, ...customLabelStyle }}>{displayLabel}</span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

// Import ELK routed edges
import { ElkRoutedEdge, ElkOrthogonalEdge } from './ElkRoutedEdge';

/**
 * Index file for edge exports
 */
export const edgeTypes = {
  smartBezier: SmartBezierEdge,
  smartSmoothStep: SmartSmoothStepEdge,
  // Issue #414: ELK routed edges with bend points
  elkRouted: ElkRoutedEdge,
  elkOrthogonal: ElkOrthogonalEdge,
};

// Re-export ELK edge types for direct import
export { ElkRoutedEdge, ElkOrthogonalEdge } from './ElkRoutedEdge';
